import { Button, Checkbox, InputNumber, message } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import { FoodSafetySurfaceCard } from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_HOLDING_MAX_MINUTES_BOUNDS,
  FOOD_SAFETY_HOLDING_SUGGESTED_RANGES,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
} from 'src/constants/foodSafety'
import { updateFoodSafetySettings } from 'src/utils/foodSafetyApi'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import {
  FOOD_SAFETY_HOLDING_TEMP_LIMITS,
  buildFoodSafetyHoldingRangePatch,
  buildFoodSafetyHoldingRangeResetPatch,
  formatFoodSafetyHoldingMaxTime,
  formatFoodSafetyHoldingRange,
  holdingRangeDraftFromSettings,
  isFoodSafetyHoldingTimedSection,
  validateFoodSafetyHoldingRangeDraft,
} from 'src/utils/foodSafetyHoldingRange'

const COPY = {
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: {
    title: 'Hot holding limit',
    body: 'Every hot holding check is measured against this — not per item.',
  },
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: {
    title: 'Cold holding limit',
    body: 'Every cold holding check is measured against this — not per item.',
  },
}

const sameDraft = (a, b) =>
  Boolean(a.noRange) === Boolean(b.noRange) &&
  String(a.tempMin ?? '') === String(b.tempMin ?? '') &&
  String(a.tempMax ?? '') === String(b.tempMax ?? '') &&
  String(a.maxMinutes ?? '') === String(b.maxMinutes ?? '')

/**
 * Venue hot / cold holding range on the Setup holding tabs. Saves only
 * `holdingRanges` — past readings keep the range they were taken under.
 */
const FoodSafetyHoldingRangeCard = ({ venueId, section, range, onSaved }) => {
  const saved = holdingRangeDraftFromSettings(section, range)
  const [draft, setDraft] = useState(saved)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const saveLock = useFoodSafetySaveLock()
  const savedKey = JSON.stringify(saved)

  useEffect(() => {
    setDraft(JSON.parse(savedKey))
    setError(null)
  }, [section, savedKey])

  const copy = COPY[section]
  if (!copy) return null
  const suggestedLabel = formatFoodSafetyHoldingRange(
    FOOD_SAFETY_HOLDING_SUGGESTED_RANGES[section],
  )
  const dirty = !sameDraft(draft, saved)
  const custom = range?.source === 'venue'
  const timed = isFoodSafetyHoldingTimedSection(section)
  const maxTime = timed ? formatFoodSafetyHoldingMaxTime(range) : ''
  const patch = next => {
    setDraft(prev => ({ ...prev, ...next }))
    setError(null)
  }

  const send = async (body, successText) => {
    if (!saveLock.tryAcquire()) return
    setSaving(true)
    try {
      const data = await updateFoodSafetySettings(venueId, body)
      message.success(successText)
      onSaved?.(data)
    } catch (err) {
      setError(err?.message || 'Could not save the limit.')
    } finally {
      saveLock.release()
      setSaving(false)
    }
  }

  const save = () => {
    const problem = validateFoodSafetyHoldingRangeDraft(section, draft)
    if (problem) {
      setError(problem)
      return
    }
    send(
      buildFoodSafetyHoldingRangePatch(section, draft),
      `${copy.title} saved`,
    )
  }

  const useSuggested = () =>
    send(
      buildFoodSafetyHoldingRangeResetPatch(section),
      `${copy.title} back to ${suggestedLabel}`,
    )

  return (
    <Card data-testid={`fs-holding-range-${section}`}>
      <H3>{copy.title}</H3>
      <BodyText>{copy.body}</BodyText>
      <Current data-testid={`fs-holding-range-current-${section}`}>
        Now:{' '}
        {formatFoodSafetyHoldingRange(
          range || FOOD_SAFETY_HOLDING_SUGGESTED_RANGES[section],
        )}
        {custom ? '' : ' (suggested)'}
        {maxTime ? ` · ${maxTime}` : ''}
      </Current>
      <Fields>
        <Field>
          <Label as='label' htmlFor={`fs-holding-range-min-input-${section}`}>
            Min °C
          </Label>
          <InputNumber
            id={`fs-holding-range-min-input-${section}`}
            value={draft.noRange ? null : draft.tempMin}
            onChange={tempMin => patch({ tempMin })}
            disabled={draft.noRange}
            min={FOOD_SAFETY_HOLDING_TEMP_LIMITS.min}
            max={FOOD_SAFETY_HOLDING_TEMP_LIMITS.max}
            placeholder='None'
            data-testid={`fs-holding-range-min-${section}`}
          />
        </Field>
        <Field>
          <Label as='label' htmlFor={`fs-holding-range-max-input-${section}`}>
            Max °C
          </Label>
          <InputNumber
            id={`fs-holding-range-max-input-${section}`}
            value={draft.noRange ? null : draft.tempMax}
            onChange={tempMax => patch({ tempMax })}
            disabled={draft.noRange}
            min={FOOD_SAFETY_HOLDING_TEMP_LIMITS.min}
            max={FOOD_SAFETY_HOLDING_TEMP_LIMITS.max}
            placeholder='None'
            data-testid={`fs-holding-range-max-${section}`}
          />
        </Field>
        {timed ? (
          <Field>
            <Label
              as='label'
              htmlFor={`fs-holding-range-time-input-${section}`}
            >
              Max hold time (minutes, optional)
            </Label>
            <InputNumber
              id={`fs-holding-range-time-input-${section}`}
              value={draft.maxMinutes === '' ? null : draft.maxMinutes}
              onChange={maxMinutes =>
                patch({ maxMinutes: maxMinutes == null ? '' : maxMinutes })
              }
              min={FOOD_SAFETY_HOLDING_MAX_MINUTES_BOUNDS.min}
              max={FOOD_SAFETY_HOLDING_MAX_MINUTES_BOUNDS.max}
              precision={0}
              placeholder='No limit'
              data-testid={`fs-holding-range-time-${section}`}
            />
          </Field>
        ) : null}
      </Fields>
      <span data-testid={`fs-holding-range-none-${section}`}>
        <Checkbox
          checked={draft.noRange}
          onChange={e => patch({ noRange: e.target.checked })}
        >
          No range — record temps without flagging them
        </Checkbox>
      </span>
      {error ? (
        <ErrorText
          role='alert'
          data-testid={`fs-holding-range-error-${section}`}
        >
          {error}
        </ErrorText>
      ) : null}
      <Actions>
        <Button
          type={dirty ? 'primary' : 'default'}
          disabled={!dirty}
          loading={saving}
          onClick={save}
          data-testid={`fs-holding-range-save-${section}`}
        >
          Save limit
        </Button>
        {custom ? (
          <Button
            type='link'
            disabled={saving}
            onClick={useSuggested}
            data-testid={`fs-holding-range-reset-${section}`}
          >
            Use suggested ({suggestedLabel})
          </Button>
        ) : null}
      </Actions>
      {timed ? (
        <Hint>
          Staff can record when hot holding started without a time limit. Set a
          maximum only when required by your venue’s food safety procedure;
          readings beyond that configured limit are flagged.
        </Hint>
      ) : null}
      <Hint>
        Changes apply to new readings. Past readings keep their limit.
      </Hint>
    </Card>
  )
}

export default FoodSafetyHoldingRangeCard

const Card = styled(FoodSafetySurfaceCard)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Current = styled.p`
  margin: 0;
  font-weight: 600;
`

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Label = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #c47a12;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`
